import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import {
  Button,
  ButtonType,
  Col,
  Radio,
  Row
} from 'syngenta-digital-cropwise-react-ui-kit';
import CropZoneIcon from 'assets/crop_zone_primary.svg';
import SelectPropertiesDrawer from 'screens/YieldV2/Loads/components/LoadsFilterDrawer/components/SelectPropertiesFilter/components/SelectPropertiesDrawer';
import {
  CropZoneMatchType,
  FilterFormikData
} from '../../Constants/filterFormikData';
import filtersString from '../../Constants/filtersString';

interface PropertiesFilterSectionProps {
  seasonIds: string[];
  hasCropZones: boolean;
}

const FORMIK_FIELD_MAP = {
  cropZoneIds: 'cropZoneIds',
  totalArea: 'totalArea',
  cropZones: 'cropZones',
  sourceType: 'sourceType'
};

const PropertiesFilterSection = ({
  seasonIds,
  hasCropZones
}: PropertiesFilterSectionProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { values, setFieldValue, setFieldTouched } = useFormikContext<
    FilterFormikData
  >();

  const cropZoneCount = values.cropZoneIds?.length ?? 0;
  const totalArea = values.totalArea ?? 0;

  const handleMatchTypeChange = (type: CropZoneMatchType) => {
    setFieldValue('matchType', type);
    setFieldTouched('matchType', true);
  };

  const handleClearSelections = () => {
    setFieldValue('cropZoneIds', []);
    setFieldValue('cropZones', []);
    setFieldValue('totalArea', null);
    setFieldValue('sourceType', null);
    setFieldTouched('cropZoneIds', true);
  };

  return (
    <>
      <div className="flex flex-col border-b border-neutral-20 px-4">
        <Row justify="space-between" align="middle" className="py-5">
          <Col>
            <h3 className="font-semibold">{filtersString.properties}</h3>
          </Col>
          <Col>
            <div className="flex items-center gap-2 text-sm text-neutral-60">
              <span>{filtersString.propertiesCropZones(cropZoneCount)}</span>
              <span className="w-px h-5 bg-neutral-30" />
              <span>{filtersString.propertiesAcreage(totalArea)}</span>
            </div>
          </Col>
        </Row>

        {hasCropZones ? (
          <div className="flex flex-col pb-5">
            <p className="text-sm text-neutral-60 mb-14px">
              {filtersString.propertiesShowRecords}
            </p>
            <div className="flex flex-col gap-3">
              <Radio
                checked={values.matchType === CropZoneMatchType.ANY}
                onChange={() => handleMatchTypeChange(CropZoneMatchType.ANY)}
              >
                {filtersString.propertiesAny}
              </Radio>
              <Radio
                checked={values.matchType === CropZoneMatchType.ALL}
                onChange={() => handleMatchTypeChange(CropZoneMatchType.ALL)}
              >
                {filtersString.propertiesAll}
              </Radio>
            </div>
            <Row gutter={[16, 0]} className="mt-26px">
              <Col flex="1">
                <Button
                  ghost
                  type={ButtonType.primary}
                  className="w-full"
                  onClick={() => setIsDrawerOpen(true)}
                  icon={<img src={CropZoneIcon} alt="" aria-hidden="true" />}
                >
                  {filtersString.propertiesSelectButton}
                </Button>
              </Col>
              <Col>
                <Button
                  type={ButtonType.outline}
                  disabled={cropZoneCount === 0}
                  onClick={handleClearSelections}
                  className="px-2"
                >
                  {filtersString.propertiesClearButton}
                </Button>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="pb-5 text-sm text-neutral-60">
            {filtersString.propertiesEmptyState}
          </div>
        )}
      </div>

      <SelectPropertiesDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        seasonIds={seasonIds}
        formikValues={FORMIK_FIELD_MAP}
      />
    </>
  );
};

export default PropertiesFilterSection;
